/* ══════════════════════════════════════════════════════════════════════
   PACING MODULE — v3
   Grouped by client. Each client shows its metrics as rows inside a card.
   Proxy metrics are visually secondary. Business days context is prominent.
   ══════════════════════════════════════════════════════════════════════ */

import { useMemo } from 'react';
import { fmt, paceVariant } from '../api/format';
import { ModuleShell } from '../components/ModuleShell';
import { Mono, StatusChip } from '../components/StatusChip';
import type { PacingResponse, PacingRow, ModuleState } from '../api/types';
import type { Filters } from '../state/useURLState';



/** Worst status across a client's metrics */
function worstStatus(rows: PacingRow[]): string {
  const order = ['off_track', 'at_risk', 'not_measurable', 'on_track'];
  for (const s of order) { if (rows.some(r => r.pace_status === s)) return s; }
  return 'on_track';
}

interface Props { moduleState: ModuleState<PacingResponse> & { refetch: () => void }; filters: Filters }

export function PacingModule({ moduleState, filters }: Props) {
  const raw = moduleState.data?.pacing || [];

  // Filter
  const filtered = useMemo(() => {
    let r = [...raw];
    if (filters.client) r = r.filter(p => p.client_id === filters.client);
    if (filters.paceStatus) r = r.filter(p => p.pace_status === filters.paceStatus);
    if (filters.search) { const q = filters.search.toLowerCase(); r = r.filter(p => p.client_name.toLowerCase().includes(q) || p.metric.includes(q)); }
    return r;
  }, [raw, filters]);

  // Group by client, preserving order
  const grouped = useMemo(() => {
    const map = new Map<string, { name: string; rows: PacingRow[] }>();
    for (const r of filtered) {
      if (!map.has(r.client_id)) map.set(r.client_id, { name: r.client_name, rows: [] });
      map.get(r.client_id)!.rows.push(r);
    }
    // Sort: worst clients first
    const statusOrder: Record<string, number> = { off_track: 0, at_risk: 1, not_measurable: 2, on_track: 3 };
    return [...map.entries()].sort((a, b) => {
      const sa = statusOrder[worstStatus(a[1].rows)] ?? 9;
      const sb = statusOrder[worstStatus(b[1].rows)] ?? 9;
      return sa - sb;
    });
  }, [filtered]);

  const offTrackClients = grouped.filter(([_, g]) => worstStatus(g.rows) === 'off_track').length;
  const biz = filtered[0];
  const progress = biz ? `${biz.biz_days_elapsed}/${biz.biz_days_total} biz days` : '';

  return (
    <ModuleShell name="pacing" title="Pacing" status={moduleState.status} error={moduleState.error} dataFreshness={moduleState.dataFreshness} fetchedAt={moduleState.fetchedAt} isStale={moduleState.isStale} truth={moduleState.truth} staleReason={moduleState.staleReason} onRetry={moduleState.refetch}
      headerRight={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {offTrackClients > 0 && <StatusChip label={`${offTrackClients} off track`} variant="error" />}
          {progress && <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', padding: '2px 8px', background: 'var(--bg-active)', borderRadius: 'var(--radius-sm)' }}>{progress}</span>}
        </div>
      }>

      {/* Truth summary — inline, not just header chips */}
      {(() => {
        const notMeasurable = filtered.filter(r => !r.measurable).length;
        const proxyCount = filtered.filter(r => r.is_proxy === 1).length;
        const total = filtered.length;
        if (notMeasurable === 0 && proxyCount === 0) return null;
        return (
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, padding: '8px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 11, color: 'var(--text-ghost)', flexWrap: 'wrap' }}>
            {notMeasurable > 0 && <span><StatusChip label={`${notMeasurable}/${total} not measurable`} variant="warning" showIcon /> — these metrics cannot be tracked from current data sources</span>}
            {proxyCount > 0 && <span><StatusChip label={`${proxyCount}/${total} proxy`} variant="proxy" showIcon /> — indirect indicators, not primary commercial targets</span>}
          </div>
        );
      })()}

      {grouped.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)', fontSize: 13, color: 'var(--text-ghost)' }}>No pacing data</div>
      ) : (
        grouped.map(([clientId, group]) => {
          const worst = worstStatus(group.rows);
          const borderColor = worst === 'off_track' ? 'var(--red)' : worst === 'at_risk' ? 'var(--yellow)' : 'var(--border-subtle)';

          return (
            <div key={clientId} style={{
              background: 'var(--bg-surface)', border: `1px solid var(--border-subtle)`,
              borderLeft: `3px solid ${borderColor}`,
              borderRadius: '0 var(--radius-lg) var(--radius-lg) 0',
              marginBottom: 12, overflow: 'hidden',
            }}>
              {/* Client header */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 16px', borderBottom: '1px solid var(--border-faint)',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: worst === 'off_track' ? 'var(--red)' : 'var(--text-primary)' }}>
                  {group.name}
                </span>
                <StatusChip label={worst.replace(/_/g, ' ')} variant={paceVariant(worst)} />
              </div>

              {/* Metric rows */}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-raised)' }}>
                    {['METRIC', 'TARGET', 'MTD', 'GAP', 'PROJECTED', 'REQ/WK', 'STATUS'].map(h => (
                      <th key={h} style={{
                        padding: '6px 16px', fontSize: 9, fontWeight: 700, color: 'var(--text-ghost)',
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                        textAlign: h === 'METRIC' ? 'left' : 'right',
                        borderBottom: '1px solid var(--border-faint)',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map(row => {
                    const isProxy = row.is_proxy === 1;
                    // Proxy rows get dimmer styling
                    const textColor = isProxy ? 'var(--text-ghost)' : 'var(--text-secondary)';

                    return (
                      <tr key={row.metric} style={{
                        borderBottom: '1px solid var(--border-faint)',
                        opacity: isProxy ? 0.75 : 1,
                      }}>
                        <td style={{ padding: '8px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 12, color: textColor, fontWeight: isProxy ? 400 : 600 }}>
                              {row.metric.replace(/_/g, ' ')}
                            </span>
                            {isProxy && <StatusChip label="proxy" variant="proxy" />}
                            {!row.measurable && <StatusChip label="not measurable" variant="muted" />}
                          </div>
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          <Mono color="var(--text-muted)">{fmt(row.target)}</Mono>
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          <Mono color={isProxy ? textColor : 'var(--text-primary)'} style={{ fontWeight: isProxy ? 400 : 700 }}>
                            {fmt(row.mtd)}
                          </Mono>
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          <Mono color={row.gap > 0 ? 'var(--red)' : 'var(--green)'} style={{ fontWeight: 700 }}>
                            {row.gap > 0 ? `−${row.gap}` : '✓'}
                          </Mono>
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          <Mono color={row.projected != null && row.projected < row.target ? 'var(--red)' : textColor}>
                            {fmt(row.projected)}
                          </Mono>
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          <Mono color={textColor}>{fmt(row.required_per_week)}</Mono>
                        </td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                          <StatusChip label={row.pace_status.replace(/_/g, ' ')} variant={paceVariant(row.pace_status)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </ModuleShell>
  );
}
